import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "Components/UI";
import Button from "Components/UI/Button";
import SearchInput from "Components/UI/SearchInput";
import LoadingStages from "Components/UI/CustomLoading/LoadingStages";
import { IDiscipline } from "Data/interfaces/Obra/IObraData";
import { IDisciplineRequest } from "Data/interfaces/Obra/IDisciplineRequest";
import { IDisciplineRequestDelete } from "Data/interfaces/Obra/IDisciplineRequestDelete";
import { useQueryStages } from "Hooks/stages/useQueryStages";
import { useQueryCreateOrUpdateDiscipline } from "Hooks/disciplines/useQueryCreateOrUpdateDiscipline";
import { useQueryDeleteDiscipline } from "Hooks/disciplines/useQueryDeleteDiscipline";
import { useQueryDisciplineLibraries } from "Hooks/disciplines/useQueryDisciplineLibraries";
import { useQueryImportDisciplineLibrary } from "Hooks/disciplines/useQueryImportDisciplineLibrary";
import { IConstructionSiteDisciplineLibraryRequest } from "Data/interfaces/Obra/IDisciplineLibraries";
import Row from "./components/Row";
import DisciplineModal, { IDisciplineFormValues } from "./components/DisciplineModal";
import DeleteModal from "./components/DeleteModal";
import EmptySearch from "./components/EmptySearch";
import Library from "./components/Library";
import {
  getDisciplineDisplayAcronym,
  getDisciplineDisplayName,
} from "./disciplines.helper";
import {
  Container,
  Content,
  EmptyList,
  HeaderContent,
  Main,
  Title,
  Toolbar,
} from "./styles";

interface IDisciplines {
  csId: number;
  disciplines?: IDiscipline[];
  loading?: boolean;
}

const Disciplines: FC<IDisciplines> = ({ csId, disciplines, loading }) => {
  const { t } = useTranslation('editObra');

  const { data: stages } = useQueryStages(csId);
  const { createOrUpdateDiscipline, isLoading: isSaving } = useQueryCreateOrUpdateDiscipline();
  const { deleteDiscipline, isLoading: isDeleting } = useQueryDeleteDiscipline();
  const {
    data: libraries,
    isFetching: listLibrariesLoading,
    refetch: refetchLibraries,
  } = useQueryDisciplineLibraries();
  const { importDisciplineLibrary, isLoading: isImporting } = useQueryImportDisciplineLibrary();

  const [search, setSearch] = useState('');
  const [sentRequest, setSentRequest] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selectedDiscipline, setSelectedDiscipline] = useState<IDiscipline | null>(null);

  const disciplinesList = useMemo(() => disciplines || [], [disciplines]);

  const sortedDisciplines = useMemo(() => (
    [...disciplinesList].sort((a, b) => (
      getDisciplineDisplayName(a).localeCompare(getDisciplineDisplayName(b), 'pt-BR', { sensitivity: 'base' })
    ))
  ), [disciplinesList]);

  const filteredDisciplines = useMemo(() => {
    if (!search) return sortedDisciplines;

    const lowerSearch = search.toLowerCase();
    return sortedDisciplines.filter((discipline) => (
      getDisciplineDisplayName(discipline).toLowerCase().includes(lowerSearch) ||
      getDisciplineDisplayAcronym(discipline).toLowerCase().includes(lowerSearch)
    ));
  }, [sortedDisciplines, search]);

  const openCreateModal = () => {
    setSelectedDiscipline(null);
    setShowModal(true);
  };

  const openEditModal = (discipline: IDiscipline) => {
    setSelectedDiscipline(discipline);
    setShowModal(true);
  };

  const openDeleteModal = (discipline: IDiscipline) => {
    setSelectedDiscipline(discipline);
    setShowDelete(true);
  };

  const openLibrary = () => {
    refetchLibraries();
    setShowLibrary(true);
  };

  const closeModals = () => {
    setShowModal(false);
    setShowDelete(false);
    setShowLibrary(false);
    setSelectedDiscipline(null);
  };

  const handleImportSubmit = (values: IConstructionSiteDisciplineLibraryRequest) => {
    importDisciplineLibrary(values);
    setSentRequest(true);
  };

  const handleSubmit = (values: IDisciplineFormValues) => {
    const request: IDisciplineRequest = {
      Id: selectedDiscipline?.ConstructionSiteDisciplinesId,
      ConstructionSiteId: csId,
      Name: values.name,
      Acronym: values.acronym,
      Icon: values.icon,
    };

    createOrUpdateDiscipline(request);
    setSentRequest(true);
  };

  const handleDelete = () => {
    if (!selectedDiscipline?.ConstructionSiteDisciplinesId) return;

    const request: IDisciplineRequestDelete = {
      ConstructionSiteId: csId,
      ConstructionSiteDisciplineId: selectedDiscipline.ConstructionSiteDisciplinesId,
    };

    deleteDiscipline(request);
    setSentRequest(true);
  };

  useEffect(() => {
    if (sentRequest && !isSaving && !isDeleting && !isImporting) {
      setSentRequest(false);
      closeModals();
    }
  }, [sentRequest, isSaving, isDeleting, isImporting]);

  return (
    <Container>
      <Title>{t('drawer.disciplines')}</Title>
      <Main>
        <Toolbar>
          <div className="searchBox">
            <SearchInput
              setSearch={setSearch}
              inputPlaceholder={t('disciplines.searchPlaceholder')}
            />
          </div>
          <Button type="text" className="toolbarBtn" onClick={openLibrary}>
            <Icon icon="listDetailVision" customSize={16} className="actionIcon" />
            <span className="textBtn">{t('disciplines.libraryButton')}</span>
          </Button>
          <Button type="text" className="toolbarBtn" onClick={openCreateModal}>
            <Icon icon="adicionar" customSize={14} className="actionIcon" />
            <span className="textBtn">{t('disciplines.createButton')}</span>
          </Button>
        </Toolbar>

        {(loading || disciplinesList.length > 0) && (
          <HeaderContent>
            <div className="column1" />
            <div className="column2">
              <span className="columnText">{t('disciplines.columnAcronym')}</span>
            </div>
            <div className="column3">
              <span className="columnText">{t('disciplines.columnDiscipline')}</span>
            </div>
            <div className="column4">
              <span className="columnText">{t('disciplines.columnStages')}</span>
            </div>
            <div className="column5" />
          </HeaderContent>
        )}

        <Content>
          {loading && <LoadingStages multiple={6} />}

          {!loading && search && filteredDisciplines.length === 0 && (
            <EmptySearch searchText={search} />
          )}

          {!loading && !search && disciplinesList.length === 0 && (
            <EmptyList>
              <span className="title">{t('disciplines.emptyListTitle')}</span>
              <span className="text">{t('disciplines.emptyListText')}</span>
              <Button type="primary" onClick={openCreateModal}>
                {t('disciplines.createButton')}
              </Button>
            </EmptyList>
          )}

          {!loading && filteredDisciplines.map((discipline) => (
            <Row
              key={discipline.ConstructionSiteDisciplinesId}
              discipline={discipline}
              stages={stages}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
            />
          ))}
        </Content>
      </Main>

      {showModal && (
        <DisciplineModal
          visible={showModal}
          editDiscipline={selectedDiscipline}
          disciplines={disciplinesList}
          loading={isSaving}
          onSubmit={handleSubmit}
          onCancel={closeModals}
        />
      )}

      {showDelete && selectedDiscipline && (
        <DeleteModal
          visible={showDelete}
          name={getDisciplineDisplayName(selectedDiscipline)}
          loading={isDeleting}
          onConfirm={handleDelete}
          onCancel={closeModals}
        />
      )}

      {showLibrary && (
        <Library
          visible={showLibrary}
          csId={csId}
          libraries={libraries}
          disciplines={disciplinesList}
          loading={isImporting}
          listLoading={listLibrariesLoading}
          onSubmit={handleImportSubmit}
          onCancel={closeModals}
        />
      )}
    </Container>
  );
};

export default Disciplines;
